import { Box, Flex, List, ListItem, useOutsideClick } from '@chakra-ui/react';
import {
  FieldHelpText,
  IconButton,
  Input,
  InputProps,
  InputVariant,
  KeyboardCodes,
  SvgSpriteConsumer,
  Text,
} from '@pluxee-design-system/core';
import { useAddressSuggestion } from 'api/locationApi';
import withFastField, { WithFastFieldProps } from 'common/forms/withFastField';
import { RuianData } from 'generated';
import { debounce } from 'lodash';
import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  memo,
  MouseEvent,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import reactStringReplace from 'react-string-replace';

interface AddressAutocompleteControlProps
  extends Omit<InputProps, 'isDisabled' | 'name' | 'onChange' | 'value' | 'rightElement'> {
  id: string;
  name: string;
  mandatory?: boolean;
  readonly?: boolean;
  skipSuccess?: boolean;
  skipVariant?: boolean;
  onClear: () => void;
  onOptionSelect: (option: RuianData) => void;
  notFoundText: string;
  loadingText: string;
}

const highlightSearchedWords = (
  data: RuianData,
  { inputValue }: { inputValue: string },
): ReactNode => {
  const label =
    inputValue !== ''
      ? reactStringReplace(data.all, new RegExp(`(${inputValue})`, 'gi'), (match, i) => (
          <Text key={i} as="span" variant="body.mediumXbold">
            {match}
          </Text>
        ))
      : data.all;
  return (
    <Text variant="body.mediumDemiBold" as="span">
      {label}
    </Text>
  );
};

// TODO: get rid-of formik
const AddressAutocompleteControl = ({
  disabled,
  field,
  name,
  notFoundText,
  loadingText,
  readonly,
  skipSuccess = false,
  skipVariant = false,
  onClear,
  onOptionSelect,
  ...inputProps
}: AddressAutocompleteControlProps & WithFastFieldProps<string>) => {
  const [inputValue, setInputValue] = useState(() => field.value);
  const [areOptionsDisplayed, setAreOptionsDisplayed] = useState<boolean>(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      setAreOptionsDisplayed(false);
      setFocusedOptionIndex(null);
    },
  });

  const isRequestEnabled = inputValue.length > 2;
  const hasError = Boolean(field.error);
  const { data, isLoading } = useAddressSuggestion(inputValue, isRequestEnabled);
  const options = data?.entries;

  const setOptionsDisplayWithValue = useCallback((value: string) => {
    if (value) setAreOptionsDisplayed(true);
    else setAreOptionsDisplayed(false);
  }, []);
  const debouncedSetInputValue = useMemo(
    () => debounce((value: string) => setInputValue(value), 1000),
    [setInputValue],
  );

  // Handlers
  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setAreOptionsDisplayed(false);
      setFocusedOptionIndex(null);
      field.onBlur(e);
    },
    [field],
  );
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      field.onChange(e);
      setOptionsDisplayWithValue(e.currentTarget.value);
      debouncedSetInputValue(e.currentTarget.value);
    },
    [debouncedSetInputValue, field, setOptionsDisplayWithValue],
  );
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (e.key === KeyboardCodes.ENTER) {
        e.preventDefault();
        if (options && focusedOptionIndex !== null) {
          const option = options[focusedOptionIndex];
          setAreOptionsDisplayed(false);
          onOptionSelect?.(option);
          setFocusedOptionIndex(null);
        }
      }

      if (areOptionsDisplayed && options?.length && e.key === KeyboardCodes.ARROW_DOWN) {
        setFocusedOptionIndex((previousFocusedOptionIndex) => {
          if (previousFocusedOptionIndex === null) {
            return 0;
          }
          if (options[previousFocusedOptionIndex + 1]) {
            return previousFocusedOptionIndex + 1;
          }
          return 0;
        });
      }
      if (areOptionsDisplayed && options?.length && e.key === KeyboardCodes.ARROW_UP) {
        setFocusedOptionIndex((previousFocusedOptionIndex) => {
          if (previousFocusedOptionIndex === null) {
            return null;
          }
          if (options[previousFocusedOptionIndex - 1]) {
            return previousFocusedOptionIndex - 1;
          }
          return options.length - 1;
        });
      }
    },
    [areOptionsDisplayed, focusedOptionIndex, onOptionSelect, options],
  );
  const handleClickOrFocus = useCallback(
    (e: MouseEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>) => {
      setOptionsDisplayWithValue(e.currentTarget.value);
    },
    [setOptionsDisplayWithValue],
  );

  return (
    <Box width="100%" position="relative" ref={containerRef}>
      <Input
        name={field.name}
        rightElement={
          field.value ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="1.5rem"
              height="1.5rem"
            >
              <IconButton
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={onClear}
                aria-label="clear input"
                variant="icon"
                icon={
                  <SvgSpriteConsumer color="semantic.icon.primary" svgId="delete24" size={24} />
                }
              />
            </Box>
          ) : null
        }
        onBlur={handleBlur}
        onClick={handleClickOrFocus}
        onFocus={handleClickOrFocus}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        value={field.value}
        {...inputProps}
        disabled={inputProps.isReadOnly || readonly ? undefined : disabled}
        variant={
          skipVariant
            ? undefined
            : inputProps.isReadOnly || readonly
              ? InputVariant.READONLY
              : field.isTouched && hasError
                ? InputVariant.ERROR
                : field.isTouched && !skipSuccess
                  ? undefined // to show X symbol (InputVariant.SUCCESS)
                  : InputVariant.FILLED
        }
      />
      {areOptionsDisplayed && (
        <List variant="autoSuggest">
          {isLoading && isRequestEnabled ? (
            <ListItem my={1}>
              <Flex align="center">{loadingText}</Flex>
            </ListItem>
          ) : (
            <>
              {options?.map((option, optionIndex) => (
                <ListItem
                  key={option.all}
                  onMouseDown={(e) => {
                    // important to not trigger input blur when clicking on an option
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAreOptionsDisplayed(false);
                    onOptionSelect?.(option);
                    setFocusedOptionIndex(null);
                  }}
                  {...(optionIndex === focusedOptionIndex
                    ? { className: 'ds-autosuggest-focused-option' }
                    : {})}
                >
                  <Flex align="center">
                    {highlightSearchedWords(option, { inputValue: field.value ?? '' })}
                  </Flex>
                </ListItem>
              ))}
              {!options?.length && (
                <ListItem my={1}>
                  <Flex align="center">{notFoundText}</Flex>
                </ListItem>
              )}
            </>
          )}
        </List>
      )}
      {field.isTouched && hasError && (
        <FieldHelpText text={field.error} variant={InputVariant.ERROR} />
      )}
    </Box>
  );
};

const MemoizedAddressAutocompleteControl = memo(AddressAutocompleteControl);

export default withFastField(MemoizedAddressAutocompleteControl);
